import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid } from '@mui/material';
import { AxiosError } from 'axios';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import {
  fetchJobBoardCandidate,
  searchJobSiteSearchBuilder,
  JobSiteSearchResponse,
} from 'wisestep-api';
import { IFilterList, WsFilterTags, WsPagination } from 'wisestep-base';
import * as yup from 'yup';

import { useWorkspaceContext } from '../../../context';
import conversationWidgetStore from '../../../features/conversationWidget/store/conversationWidgetStore';
import { ITrackCvViewJobBoards } from '../../../features/conversationWidget/utils/cvHelper';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import CandidateCard from '../components/CandidateCard';
import NativeSearchForm from '../components/NativeSearchForm';
import BasicSearchForm from '../types/IBasicSearchForm';
import IQueryParam from '../types/IQueryParam';
import basicSearchDefaultValues, { generateJobBoardURL } from '../utils/nativeSearchHelper';

import AdvancedFiltersNew from './components/AdavncedFiltersNew';
import IJobSiteAdvanceSearch, { AdvancedSearchKeys } from './types/JobSiteAdvanceSearch';
import constructQuery from './utils/constructQuery';
import defaultAdvanceFilterValues from './utils/defaultAdvanceFilterValues';
import { getJobSiteCandidateDetails } from './utils/JobSiteHelper';
import { pushSearchFilters } from './utils/pushSearchTags';

const JobSiteNew = () => {
  const selectedJob = conversationWidgetStore((state) => state.selectedJob);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [jobSiteResponse, setJobSiteResponse] = useState<JobSiteSearchResponse>();
  const [appliedFilters, setAppliedFilters] = useState<IFilterList[]>([]);
  const setCvViewSourcePage = useConversationWidgetStore((state) => state.setCvViewSourcePage);
  const methods = useForm<BasicSearchForm & IJobSiteAdvanceSearch>({
    defaultValues: {
      ...basicSearchDefaultValues,
      ...defaultAdvanceFilterValues,
      lastActive: '1829',
      distance: 500,
    },
    resolver: yupResolver(
      yup.object().shape({ query: yup.string().required('Search query is required') })
    ),
  });
  const setAdvanceFilterField = (targetField: AdvancedSearchKeys, value) =>
    methods.setValue(targetField, value);
  const emptyAdvanceFieldValues = {
    ...defaultAdvanceFilterValues,
    ...{ excludeSocialOnlyProfiles: false, additionalFilters: [], contactMethod: [] },
  };
  const workspaceContext = useWorkspaceContext();
  const page = methods.watch('page');
  const accountId = methods.watch('accountId');
  const performSearch = async (values: BasicSearchForm & IJobSiteAdvanceSearch) => {
    setAppliedFilters(pushSearchFilters(values));
    try {
      values.workspaceId = workspaceContext.id;
      const response = await searchJobSiteSearchBuilder(constructQuery(values));
      if (!isEmpty(response.data.Error)) {
        setNotification(failed(response.data.Error));
        return;
      }
      setJobSiteResponse(response.data);
    } catch (e) {
      console.log('error', e);
    }
  };
  useEffect(() => {
    if (methods.formState.errors.query?.message) {
      setNotification(failed('Search Query is Required'));
    }
  }, [methods.formState.errors.query, setNotification]);

  const onDeleteFilterTag = (tag: string) => {
    const idOfChip = appliedFilters.find((existChip) => existChip.tag === tag)?.id ?? '';
    if (!isEmpty(idOfChip)) {
      setAdvanceFilterField(idOfChip as AdvancedSearchKeys, emptyAdvanceFieldValues[idOfChip]);
      methods.handleSubmit(performSearch)();
      const index = appliedFilters.findIndex((existChip) => existChip.id === idOfChip);
      appliedFilters.splice(index, 1);
      setAppliedFilters([...appliedFilters]);
    }
  };
  return (
    <FormProvider {...methods}>
      <Grid container display="flex" justifyContent="space-between">
        <Grid item xs={9}>
          <Box ml={2}>
            <NativeSearchForm jobBoardName="JOB_SITE" performSearch={performSearch} />
          </Box>
          <WsFilterTags filtersList={appliedFilters} onDelete={onDeleteFilterTag} />
          {jobSiteResponse &&
            jobSiteResponse.TotalResultsCount &&
            jobSiteResponse.TotalResultsCount > 0 && (
              <WsPagination
                count={Math.ceil(jobSiteResponse.TotalResultsCount / 50)}
                onChange={(_, value) => {
                  methods.setValue('page', value + 1);
                  methods.handleSubmit(performSearch)();
                }}
                itemsPerPage={50}
                pageNumber={page - 1}
                defaultPage={page}
                isPageCount={true}
                type="Candidates"
                totalCount={jobSiteResponse.TotalResultsCount}
              />
            )}
          {jobSiteResponse?.Candidates.map((candidate) => (
            <Grid my={2} key={candidate.Id} item xs={12}>
              <CandidateCard
                candidateId={'' + candidate.Id}
                title={`${candidate.Title ? candidate.Title : ''}
                ${candidate.ForeName ? candidate.ForeName : ''} 
                ${candidate.Surname ? candidate.Surname : ''}`}
                name={`${candidate.Title && candidate.Title} ${
                  candidate.ForeName && candidate.ForeName
                } ${candidate.Surname && candidate.Surname}`}
                imgSrc={''}
                isDownloaded={
                  jobSiteResponse.existing?.some(
                    (existingCandidate) =>
                      existingCandidate.candidateJobBoardId === candidate.Id + ''
                  ) ?? false
                }
                skills={candidate.KeySkills ? candidate.KeySkills.split(',') : []}
                dateLastActive={'' + candidate.LastActiveDate}
                dateResumeLastUpdated={candidate.LastUpdatedDate}
                onViewCvClick={async (candidateId) => {
                  try {
                    const data = await fetchJobBoardCandidate({
                      jobBoardAccountId: accountId,
                      jobBoardId: candidateId,
                      force: false,
                      harvestingId: '',
                      candidateData: {
                        jobId: selectedJob?.id ?? '',
                        team: selectedJob?.activeWorkAssignment?.team ?? null,
                      },
                    });
                    const candidateData = data.newCandidateData;
                    const params: IQueryParam = {
                      resumeUpdatedOn: candidate.LastUpdatedDate,
                      accountId: accountId,
                      jobBoardId: candidateId,
                      query: encodeURI(methods.getValues('query') ?? ''),
                      jobBoard: 'JobSite',
                    };
                    setCvViewSourcePage(ITrackCvViewJobBoards.JOBSITESEARCH);
                    const paramId = `param${uuidv4()}`;
                    localStorage.setItem(paramId, getEncodedQueryParams(params));
                    openUrlInNewTab(
                      `/candidate-layout/profile?id=${
                        candidateData?.newCandidateData?.id
                      }&array=${[]}&q=${getEncodedQueryParams(
                        params
                      )}&backwardpathArray=${getEncodedQueryParams(
                        JSON.stringify(generateJobBoardURL())
                      )}`
                    );
                  } catch (error) {
                    const err = error as AxiosError;
                    if (err.response?.data.status) {
                      setNotification(failed(err.response?.data.status));
                    } else {
                      setNotification(failed('There was some problem in fetching the candidate.'));
                    }
                  }
                }}
                detailItems={getJobSiteCandidateDetails(candidate)}
              />
            </Grid>
          ))}
        </Grid>
        <Grid item xs={2.8}>
          <AdvancedFiltersNew performSearch={performSearch} />
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default JobSiteNew;
